import { useCallback, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useRoster } from '../lib/roster'
import { fetchAll } from '../lib/fetchall'
import { supabase } from '../lib/supabase'
import { LANE_FROM, REPLY_TO } from '../lib/campaigns'
import { signatureHtml } from '../lib/emailrules'
import { hideCampaign, loadHiddenCampaignIds, unhideCampaign } from '../lib/hiddencampaigns'
import {
  NO_PROGRESS,
  checkProgress,
  driftFor,
  estimateRemainingMs,
  formatEta,
  hasDrift,
  runSend,
  sendTest,
  stopSend,
  type Drift,
  type PaceSample,
  type Progress,
  type SavedBlast,
  type SendOutcome,
} from '../lib/sender'

/**
 * Bulk email: the last screen before 785 people hear from Renee.
 *
 * The composer saves a blast. It does not send one, and nothing on a timer ever
 * will. This is the only place a bulk send can begin, and beginning one takes
 * four deliberate acts: open the card, read the message, send yourself a test,
 * and type the number of emails about to go.
 *
 * ── WHY TYPING THE NUMBER ─────────────────────────────────────────────────
 *
 * Every other confirmation in this app is a button, because everything else can
 * be undone. This cannot. A number typed by hand is the cheapest available way
 * of making somebody look at the number — and the number is exactly the thing
 * that goes wrong, because it moves between saving and sending.
 *
 * **What she types is what is sent to the server**, not what this screen
 * calculated, and it is **how many emails are about to go** rather than how big
 * the original list was. On a resumed send those are different numbers, and
 * asking her to confirm 785 to send 485 is a ceremony performed over the wrong
 * figure.
 *
 * ── AND WHY THE REFUSAL IS RECOVERABLE ────────────────────────────────────
 *
 * When the server's count differs it refuses and says so. The first version
 * then asked her to "confirm the new number" while the input would only accept
 * the old one — an instruction the interface forbade. Now a refusal reloads the
 * roster and the tags, adopts the server's number, and asks her to confirm
 * that. A refusal you cannot act on is worse than no refusal.
 */

type OccState = { status: string; recipientCount: number | null; lastStoppedKind: string | null }

export default function Bulk() {
  const [params] = useSearchParams()
  const {
    agents,
    error: rosterError,
    reloadAgents,
    agentsLoading,
    tagsByAgent,
    tagsError,
    tagsLoaded,
    tagsLoading,
    reloadTags,
  } = useRoster()

  const [blasts, setBlasts] = useState<SavedBlast[] | null>(null)
  const [occurrences, setOccurrences] = useState<Map<string, OccState>>(new Map())
  const [error, setError] = useState<string | null>(null)
  const [open, setOpen] = useState<string | null>(null)
  const [typed, setTyped] = useState('')
  const [busy, setBusy] = useState<string | null>(null)
  /**
   * Which campaign is mid-send, kept apart from `busy`.
   *
   * `busy` was doing two jobs — "which button is spinning" and "is a send
   * running" — so opening any other card cleared it, which switched off the
   * page-level warning, unlocked the running card's Close button and turned its
   * label back from "Sending… 340 of 785" to an invitation to press Send. The
   * guards were being undone by the one control left enabled.
   */
  const [sendingId, setSendingId] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null)
  const [confirmStop, setConfirmStop] = useState<string | null>(null)
  const [outcome, setOutcome] = useState<Record<string, SendOutcome>>({})
  const [progress, setProgress] = useState<Record<string, Progress>>({})
  const [testedTo, setTestedTo] = useState<Record<string, string>>({})
  /** The number the server insisted on, after it refused the one she typed. */
  const [serverCount, setServerCount] = useState<Record<string, number>>({})
  /**
   * The pace curve for whichever send is running — every `{ t, sent }` point
   * `onProgress` has produced since THIS attempt (or resume) began. Reset at
   * the start of every `send()` call, not accumulated across attempts: a
   * resume after a stop is its own run with its own pace, and averaging in
   * points from before the stop would understate how long the resume itself
   * is taking. Feeds `estimateRemainingMs` in `lib/sender.ts` for the live
   * ETA below.
   */
  const [pace, setPace] = useState<Record<string, PaceSample[]>>({})

  /**
   * The other half of queue item 4 — sent campaigns can pile up forever, so
   * she can hide one from the default view without losing the record (that
   * stays in `campaign_sends` regardless; deleting it for real is the
   * separate 30-day Worker's job, not this). `showHidden` deliberately does
   * NOT persist — same reasoning `Tasks.tsx`'s own `showDone` toggle uses:
   * it resets to "just the ones I still care about" every time she opens
   * this tab fresh, and it costs her one click on the rare visit where she
   * specifically wants to check what's hidden.
   */
  const [hiddenIds, setHiddenIds] = useState<Set<string>>(() => loadHiddenCampaignIds())
  const [showHidden, setShowHidden] = useState(false)
  const [hideError, setHideError] = useState<string | null>(null)

  const hide = (id: string) => {
    const r = hideCampaign(id)
    setHiddenIds(r.ids)
    setHideError(r.stored ? null : 'This browser is blocking storage, so hiding it will not survive a reload.')
  }
  const unhide = (id: string) => {
    const r = unhideCampaign(id)
    setHiddenIds(r.ids)
    setHideError(r.stored ? null : 'This browser is blocking storage, so putting it back will not survive a reload.')
  }

  /**
   * The guardrail if she tries to close the tab or the browser mid-send.
   *
   * Modern browsers no longer allow custom wording in this prompt — setting
   * `returnValue` only controls WHETHER the browser's own generic "Leave
   * site?" text appears, not what it says. Cannot catch the laptop sleeping,
   * losing the network, or a crash — nothing can, since none of those give
   * the page a chance to run anything at all. Registered only while a send
   * is actually running (`sendingId`), not for the whole time this screen is
   * open, so leaving an idle Bulk tab never prompts.
   */
  useEffect(() => {
    if (!sendingId) return
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [sendingId])

  const load = useCallback(async () => {
    const c = await fetchAll<SavedBlast>(
      'campaigns',
      // `is_enabled` is deliberately absent — see the note in `sender.ts`.
      'id,name,subject,body,audience,created_at',
      ['created_at', 'id'],
    )
    if (c.error) {
      setError(c.error)
      setBlasts(null)
      return
    }
    setError(null)
    setBlasts(c.rows.filter((b) => b.id.startsWith('manual-')))

    /**
     * Every occurrence, paged.
     *
     * Both statuses are kept, not just `sent`. Keeping only the finished ones
     * meant a send interrupted halfway came back after a reload as a card
     * headed "held" offering to send to all 785 — with four hundred of them
     * already having it. The browser was fetching the answer and discarding it.
     */
    const o = await fetchAll<{
      campaign_id: string
      status: string
      recipient_count: number | null
      last_stopped_kind: string | null
    }>('campaign_occurrences', 'campaign_id,status,recipient_count,last_stopped_kind', ['campaign_id', 'id'])
    // `recipient_count` comes along so a finished send can say how many it
    // reached on a fresh page load, before anybody opens the card.
    // `last_stopped_kind` is how a card knows it was permanently stopped
    // (see `stopped` below) rather than merely paused mid-send.
    setOccurrences(
      new Map(
        o.rows.map((r) => [
          r.campaign_id,
          { status: r.status, recipientCount: r.recipient_count, lastStoppedKind: r.last_stopped_kind },
        ]),
      ),
    )
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  /**
   * `?open=<id>` — the deep link `Drafts.tsx`'s "Scheduled sends" section
   * uses ("Review & send" there just navigates here with this set). One card
   * pre-opened, so pressing it feels like it landed her directly on the
   * thing rather than handing her a list to search.
   *
   * Deliberately NOT calling `toggle`/`look` below — both are ordinary
   * consts defined after this component's early-return guards, so a Hook
   * here (which must run unconditionally, before any return) cannot reach
   * them. The few lines of `look`'s body are inlined instead of restructuring
   * already-hardened code to share them, for one small effect.
   *
   * `consumedOpen` stops this from re-opening a card she has deliberately
   * closed — it fires once, the first time `blasts` has loaded with the id
   * present, and never again for the life of this mount.
   */
  const consumedOpen = useRef(false)
  useEffect(() => {
    if (consumedOpen.current) return
    if (!blasts) return
    const wanted = params.get('open')
    if (!wanted) {
      consumedOpen.current = true
      return
    }
    if (!blasts.some((b) => b.id === wanted)) return // wait for it to appear, or for Cancel below to give up
    consumedOpen.current = true
    setOpen(wanted)
    setTyped('')
    setConfirmDelete(null)
    setConfirmStop(null)
    if (occurrences.has(wanted)) {
      void (async () => {
        setBusy(wanted + 'look')
        const r = await checkProgress(wanted)
        setBusy(null)
        setProgress((p) => ({ ...p, [wanted]: r.progress }))
        setOutcome((p) => ({ ...p, [wanted]: r.error ? r : { ok: true, progress: r.progress } }))
        void load()
      })()
    }
  }, [blasts, occurrences, params, load])

  if (rosterError && !agents) {
    return (
      <div className="board tpl">
        <section className="sec">
          <p className="error">The agent list did not load, so nothing here can be trusted. {rosterError}</p>
          <div className="savebar">
            <button className="btn" disabled={agentsLoading} onClick={() => reloadAgents()}>
              {agentsLoading ? 'Trying…' : 'Try again'}
            </button>
          </div>
        </section>
      </div>
    )
  }

  /**
   * The tags are part of "is the roster here", not a detail to fill in later.
   *
   * `driftFor` answers "who is tagged no-bulk-email" against `tagsByAgent`, and
   * an empty map answers "nobody". Rendering before they land shows 785 with no
   * exclusions and a live send button.
   */
  if (!agents || !tagsLoaded || !blasts) return <div className="empty">{error ?? 'Loading…'}</div>

  const look = async (id: string) => {
    setBusy(id + 'look')
    const r = await checkProgress(id)
    setBusy(null)
    setProgress((p) => ({ ...p, [id]: r.progress }))
    // Cleared on success, not left alone. A stale "the send stopped partway —
    // press Send again" used to survive a clean check and sit underneath "This
    // has been sent", pointing at a button that no longer exists.
    setOutcome((p) => ({ ...p, [id]: r.error ? r : { ok: true, progress: r.progress } }))
    void load()
  }

  const toggle = (b: SavedBlast) => {
    const opening = open !== b.id
    setOpen(opening ? b.id : null)
    setTyped('')
    setConfirmDelete(null)
    setConfirmStop(null)
    /*
      `serverCount` is deliberately NOT cleared here. It is only ever set by a
      refusal that has just happened, and `send()` clears it on every other
      outcome — so wiping it on close threw away the one correction the screen
      had, and reopening offered the number the server had just refused.
    */
    /*
      If a send of this has already been started, the browser has no idea how
      far it got — that lives in the database. Ask, rather than showing her a
      card that describes an untouched blast.
    */
    if (opening && occurrences.has(b.id)) void look(b.id)
  }

  const test = async (b: SavedBlast) => {
    setBusy(b.id + 'test')
    const r = await sendTest(b.id)
    setBusy(null)
    if (r.ok && r.to) {
      setTestedTo((p) => ({ ...p, [b.id]: r.to! }))
      setOutcome((p) => ({ ...p, [b.id]: { ok: true, progress: NO_PROGRESS } }))
    } else {
      setOutcome((p) => ({ ...p, [b.id]: { ok: false, progress: NO_PROGRESS, error: r.error } }))
    }
  }

  const send = async (b: SavedBlast) => {
    setBusy(b.id + 'send')
    setSendingId(b.id)
    setOutcome((p) => ({ ...p, [b.id]: { ok: true, progress: { ...NO_PROGRESS, stage: 'starting' } } }))
    // Fresh pace curve for THIS attempt — see the state's own comment for why
    // a resume must not inherit points from before the stop.
    setPace((p) => ({ ...p, [b.id]: [] }))
    /**
     * Asked here, not on mount — a permission prompt with no user gesture
     * behind it is the kind of thing browsers are moving to ignore outright,
     * and pressing Send is the first moment this screen has one. `'default'`
     * only: asking again after she has already said yes or no would just be
     * ignored by the browser, but there is no reason to call it repeatedly.
     */
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      void Notification.requestPermission()
    }

    let r: SendOutcome
    try {
      r = await runSend({
        campaignId: b.id,
        typedCount: Number(typed.trim()),
        onProgress: (p) => {
          setProgress((prev) => ({ ...prev, [b.id]: p }))
          setPace((prev) => ({ ...prev, [b.id]: [...(prev[b.id] ?? []), { t: Date.now(), sent: p.sent }] }))
        },
      })
    } catch (err) {
      console.error('send failed', err)
      r = {
        ok: false,
        progress: NO_PROGRESS,
        error: 'Something went wrong here rather than at the other end. Press Check progress before doing anything else.',
      }
    } finally {
      /**
       * Released in a `finally`, because `sendingId` locks the whole page.
       *
       * It disables every control on every card and puts up a banner telling
       * her to keep the page open — so if this were left set by a throw, the
       * only way out would be the reload the banner is telling her not to do.
       */
      setBusy(null)
      setSendingId(null)
    }
    setOutcome((p) => ({ ...p, [b.id]: r }))
    setTyped('')
    // For when the tab is open but she has switched away to something else —
    // the on-screen failure text (`res?.error` below) already covers the
    // case where she is watching. Does nothing without permission, and does
    // nothing at all for a send the tab was already closed for; that gap is
    // the separate watchdog-alert idea, still held on Chris's decision.
    notifySendResult(b, r)

    /**
     * A refusal over the count is recoverable, and this is where that happens:
     * take the server's number, then go and get fresh agents and tags so the
     * rest of the card agrees with it.
     */
    if (typeof r.serverCount === 'number') {
      setServerCount((p) => ({ ...p, [b.id]: r.serverCount! }))
      reloadAgents()
      reloadTags()
    } else {
      setServerCount((p) => {
        const next = { ...p }
        delete next[b.id]
        return next
      })
    }
    void load()
  }

  const remove = async (b: SavedBlast) => {
    setBusy(b.id + 'del')
    const { error: delErr } = await supabase.from('campaigns').delete().eq('id', b.id)
    setBusy(null)
    setConfirmDelete(null)
    if (delErr) {
      setError('That could not be deleted.')
      return
    }
    void load()
  }

  const stop = async (b: SavedBlast) => {
    setBusy(b.id + 'stop')
    const err = await stopSend(b.id)
    setBusy(null)
    setConfirmStop(null)
    if (err) {
      setError('That could not be stopped.')
      return
    }
    void load()
  }

  const shownBlasts = blasts.filter((b) => showHidden || !hiddenIds.has(b.id))
  const hiddenCount = blasts.filter((b) => hiddenIds.has(b.id)).length

  return (
    <div className="board tpl">
      {error && <p className="error">{error}</p>}

      {/*
        Said once, at the top, where it is visible whatever card is open — the
        instruction used to live inside the expanded card, so collapsing it made
        the warning disappear while the send carried on invisibly.
      */}
      {sendingId && (
        <p className="error">
          A bulk email is sending right now. <strong>Keep this page open until it finishes.</strong>{' '}
          Everything else here is paused until it does.
        </p>
      )}

      {tagsError && (
        <>
          <p className="error">
            The agent tags did not load, so this cannot tell who has asked not to receive bulk
            email. Sending is switched off until they do. {tagsError}
          </p>
          <div className="savebar">
            <button className="btn" disabled={tagsLoading} onClick={() => reloadTags()}>
              {tagsLoading ? 'Trying…' : 'Try loading the tags again'}
            </button>
          </div>
        </>
      )}

      {hideError && <p className="error">{hideError}</p>}

      <div className="notice">
        Bulk emails you have saved. <strong>{blasts.length} saved.</strong> Nothing here sends on its
        own — one only goes when you open it, send yourself a test, and type the number of emails to
        confirm.
      </div>

      {/*
        Same shape as `Tasks.tsx`'s own done-tasks toggle: only rendered when
        there is something to show, and says the number rather than leaving
        her to guess what pressing it will do.
      */}
      {hiddenCount > 0 && (
        <div className="toolbar" style={{ padding: '4px 0 10px' }}>
          <button className="btn ghost sm" onClick={() => setShowHidden((v) => !v)}>
            {showHidden ? `Hide the ${hiddenCount} hidden` : `Show ${hiddenCount} hidden`}
          </button>
        </div>
      )}

      {blasts.length === 0 && (
        <div className="hint">
          Nothing saved. Bulk emails are made on the roster: filter or tick the people you want,
          press Email, then choose "Same message to everybody".
        </div>
      )}

      {/*
        Distinct from the zero-saved case above — there IS something saved,
        it's just all hidden right now. "Nothing saved" would read as though
        the campaigns themselves were gone, not just out of view.
      */}
      {blasts.length > 0 && shownBlasts.length === 0 && (
        <div className="hint">
          Everything saved is hidden. Press <strong>Show {hiddenCount} hidden</strong> above to see
          it again.
        </div>
      )}

      {shownBlasts.map((b) => {
        const drift = driftFor(b, agents, tagsByAgent)
        const occ = occurrences.get(b.id)
        const prog = progress[b.id]
        /**
         * Both sources, because they land at different times.
         *
         * `occurrences` is refreshed by an async `load()`; `prog` is set the
         * moment the send returns. Reading only the first meant that for the
         * second or two between them the card said **held · 0 agents will
         * receive this · Nobody on this list can be emailed any more. Build it
         * again from the roster** — directly after she watched "Sending… 785 of
         * 785" finish. Acting on that sentence sends it all a second time.
         */
        const done = occ?.status === 'sent' || prog?.done === true
        /**
         * Permanently abandoned partway — `stopSend`/`skipHeldBulkSend`
         * wrote this, or it was set by hand the one time this happened
         * before the button existed
         * (`claude/team-connect-9-8-send-stopped-2026-09-08.md`). Checked
         * before `started` so the two badges are mutually exclusive: a
         * stopped send is not "in progress", it is finished trying.
         */
        const stopped = !done && occ?.lastStoppedKind === 'fatal'
        const started = !done && !stopped && (!!occ || (!!prog && prog.total > 0))
        const isHidden = hiddenIds.has(b.id)
        const res = outcome[b.id]
        const running = sendingId === b.id
        const isOpen = open === b.id

        /**
         * How many emails the next press will actually send.
         *
         * Once a send exists, that is the number of rows still waiting — not
         * the size of the original list. `serverCount` is the server's own
         * answer after it has refused one, and it wins until the card is
         * closed or another attempt supersedes it.
         */
        const count = serverCount[b.id] ?? (prog && prog.total > 0 ? prog.pending : drift.recipients.length)
        const confirmed = typed.trim() !== '' && typed.trim() === String(count)

        return (
          <section className="sec" key={b.id} style={{ marginBottom: 14 }}>
            <h3>
              {b.name}
              {done && <span className="pill s-sent">sent</span>}
              {stopped && <span className="pill s-failed">stopped</span>}
              {started && <span className="pill s-pending">started</span>}
              {/* Mutually exclusive with the rest — reading `!occ` alone
                  put "sent" and "held" side by side for the second between a
                  send finishing and `load()` landing. */}
              {!done && !stopped && !started && <span className="pill s-drafted">held</span>}
            </h3>

            <div className="emeta">
              <span>From {LANE_FROM.blast} · replies to {REPLY_TO}</span>
              <span className="hint">saved {new Date(b.created_at).toLocaleString()}</span>
            </div>

            {done ? (
              /*
                Says what is true rather than making a promise the server does
                not make. "It cannot be sent again" was enforced only by this
                screen hiding a button; what actually holds is that one campaign
                has one send, and reaching these people again means building a
                new one.
              */
              <p className="cnt">
                This has been sent
                {prog && prog.sent > 0
                  ? ` to ${prog.sent} ${prog.sent === 1 ? 'agent' : 'agents'}`
                  : occ?.recipientCount
                    ? ` to ${occ.recipientCount} ${occ.recipientCount === 1 ? 'agent' : 'agents'}`
                    : ''}
                . Reaching
                these people again means building a new bulk email from the roster.
                {/*
                  Refusals belong in the completion sentence, not buried in a
                  progress line inside the expanded card. A send that ends "sent
                  to 685" over a hundred addresses the email service would not
                  accept is a report that reads like a success.
                */}
                {/*
                  No pointer to What's Missing: that screen lists gaps in an
                  agent's record, and an address the email service refused is
                  one that passed every check this app makes. Sending her to a
                  tab where those people cannot be found is worse than saying
                  nothing.
                */}
                {prog && prog.failed > 0 && (
                  <>
                    {' '}
                    <strong>{prog.failed} did not go</strong> — the email service would not accept
                    those addresses. Tell Chris and he can get you the list.
                  </>
                )}
                {prog && prog.suppressed > 0 && (
                  <>
                    {' '}
                    {prog.suppressed} were taken off along the way, because they left or were tagged
                    after it started.
                  </>
                )}
                {/*
                  "A further" because these are not part of the sent figure —
                  `sent` and `unknown` count different rows, and "35 of them"
                  read as a subset of the number just quoted.
                */}
                {prog && prog.unknown > 0 && (
                  <>
                    {' '}
                    A further <strong>{prog.unknown}</strong> were handed over without an answer
                    coming back — check Resend if you need to be sure those arrived.
                  </>
                )}
              </p>
            ) : null}

            {/*
              "Hide from this list" moves a finished-with campaign out of the
              default view without touching the database — the record it's
              part of (`campaign_sends`, the only account of who actually
              received this) is untouched either way, and stays untouched
              right up until the separate 30-day Worker deletes it for real.
              Offered once a send is `done`, or once it is `stopped`
              (2026-09-08 — see `stopSend`) — never for a held or genuinely
              in-progress one, so "hidden" keeps meaning "nothing more is
              going to happen here" rather than "campaign I don't want to
              look at right now".
            */}
            {(done || stopped) && (
              <p className="hint">
                {isHidden ? (
                  <>
                    Hidden from the list.{' '}
                    <button className="btn ghost sm" onClick={() => unhide(b.id)}>
                      Put it back
                    </button>
                  </>
                ) : (
                  <button className="btn ghost sm" onClick={() => hide(b.id)}>
                    Hide from this list
                  </button>
                )}
              </p>
            )}

            {done ? null : stopped ? (
              <p className="cnt">
                This was stopped before it finished.
                {prog && prog.total > 0
                  ? ` ${prog.sent} of ${prog.total} went out; the remaining ${prog.pending} will not ` +
                    'be sent on their own.'
                  : occ?.recipientCount
                    ? ` ${occ.recipientCount} had gone out when it was stopped; the rest will not be ` +
                      'sent on their own.'
                    : ' The rest will not be sent on their own.'}{' '}
                Press Check progress for the exact count, or Carry on below if this should actually
                finish after all.
              </p>
            ) : started ? (
              <p className="cnt">
                A send of this was started and has not finished.
                {prog && prog.total > 0
                  ? ` ${prog.sent} of ${prog.total} have gone; ${prog.pending} still to go.`
                  : ' Checking where it got to…'}
              </p>
            ) : (
              <>
                <p className="cnt">
                  {count} {count === 1 ? 'agent' : 'agents'} will receive this
                  {drift.saved !== drift.recipients.length && <> — it was {drift.saved} when you saved it</>}
                </p>
                <DriftNote drift={drift} />
              </>
            )}

            {typeof serverCount[b.id] === 'number' && (
              <p className="hint">
                When you last tried, the sending service counted {serverCount[b.id]}. That is the
                number it will accept.
              </p>
            )}

            {/*
              Not disabled by `busy` in general — she must always be able to
              look at a card. It IS disabled during a send, because collapsing
              the running card used to hide the progress and the instruction
              while the loop carried on invisibly.
            */}
            <button className="btn ghost sm" disabled={running} onClick={() => toggle(b)}>
              {isOpen ? 'Close' : 'Open it'}
            </button>

            {isOpen && (
              <>
                <div className="dbody" style={{ marginTop: 10 }}>
                  <strong>{b.subject}</strong>
                  {'\n\n'}
                  {b.body}
                </div>

                {/*
                  Renee's HiHello card, added automatically to every blast —
                  claude/bulk-email-signature-design-2026-08-23.md, item 6:
                  "Bulk.tsx's own preview ... should show the signature too
                  once this is built, not just the body text." Not part of
                  `b.body` and not editable here, same reasoning
                  `send-campaign.ts` appends it at send time rather than
                  storing it per-campaign: one place to update her card
                  later, and nothing she types can accidentally remove it.
                  Rendered from the same `signatureHtml()` the real send
                  builds from, so this is what actually goes out, not a
                  hand-copied approximation of it.
                */}
                <div
                  className="hint"
                  style={{ marginTop: 4 }}
                  dangerouslySetInnerHTML={{ __html: signatureHtml() }}
                />

                {res?.error && <p className={res.ok ? 'hint' : 'error'}>{res.error}</p>}
                {testedTo[b.id] && !res?.error && (
                  <p className="hint">
                    Test sent to {testedTo[b.id]}. Nobody else received it. Check the From line, the
                    signature, and how it reads on a phone.
                  </p>
                )}

                {prog && prog.total > 0 && (
                  <p className="cnt">
                    {prog.sent} of {prog.total} sent
                    {prog.pending > 0 && ` · ${prog.pending} still to go`}
                    {prog.inFlight > 0 && ` · ${prog.inFlight} going out right now`}
                    {prog.suppressed > 0 && ` · ${prog.suppressed} taken off since it started`}
                    {prog.failed > 0 && ` · ${prog.failed} refused`}
                    {/*
                      Reported whenever it is non-zero, and only counted once a
                      row has been sitting claimed for a few minutes — otherwise
                      every healthy send in progress reads as an emergency and
                      she learns to ignore the number that matters.
                    */}
                    {prog.unknown > 0 && (
                      <>
                        {' '}· <strong>{prog.unknown} unknown</strong> — handed over with no answer
                        back. Those are not being sent again automatically; check Resend before
                        deciding.
                      </>
                    )}
                  </p>
                )}

                {!done && (
                  <>
                    <div className="savebar" style={{ marginTop: 12 }}>
                      <button className="btn" disabled={!!busy || !!tagsError} onClick={() => void test(b)}>
                        {busy === b.id + 'test' ? 'Sending…' : 'Send a test to me'}
                      </button>
                      <button className="btn ghost" disabled={!!busy} onClick={() => void look(b.id)}>
                        {busy === b.id + 'look' ? 'Checking…' : 'Check progress'}
                      </button>
                    </div>

                    {count === 0 ? (
                      <p className="hint" style={{ marginTop: 14 }}>
                        {started || stopped
                          ? 'There is nothing left to send. Press Check progress to see how it ended.'
                          : 'Nobody on this list can be emailed any more — everyone on it has left, been tagged, or has no address. Build it again from the roster.'}
                      </p>
                    ) : (
                      <>
                        <div className="hint" style={{ marginTop: 14, marginBottom: 4 }}>
                          {/*
                            Whether this reads "remaining" or "all" tracks
                            whether a send has EVER begun (`started ||
                            stopped`), not whether it is currently active —
                            a stopped send resuming via Carry on is still a
                            resume, not a fresh send to a freshly-counted
                            list.
                          */}
                          {started || stopped ? (
                            <>
                              To send the remaining {count}, type <strong>{count}</strong> here.
                            </>
                          ) : (
                            <>
                              To send it to all {count}, type <strong>{count}</strong> here.
                            </>
                          )}
                        </div>
                        <input
                          className="areasel-d"
                          value={typed}
                          inputMode="numeric"
                          disabled={running}
                          placeholder={String(count)}
                          onChange={(e) => setTyped(e.target.value)}
                          style={{ maxWidth: 160 }}
                        />
                        <div className="savebar">
                          <button
                            className="btn"
                            disabled={!confirmed || !!busy || !!tagsError}
                            onClick={() => void send(b)}
                          >
                            {running
                              ? prog && prog.total
                                ? `Sending… ${prog.sent} of ${prog.total}`
                                : 'Starting…'
                              : started || stopped
                                ? `Carry on — send the remaining ${count}`
                                : `Send it to ${count} ${count === 1 ? 'agent' : 'agents'}`}
                          </button>
                          <span className="status">
                            {running
                              ? sendingStatus(prog, pace[b.id])
                              : 'This cannot be undone, and nobody reads the copies.'}
                          </span>
                        </div>
                      </>
                    )}

                    {/*
                      Stop is the occurrence-exists half of the same choice
                      Delete makes below — offered only while a send is
                      genuinely still going (`occ && !stopped`), gone once it
                      already is, since stopping an already-stopped send is
                      not a thing. Built 2026-09-08 so this never again needs
                      a SQL editor — see `stopSend` in `lib/sender.ts`.
                    */}
                    {occ && !stopped && (
                      <div className="savebar" style={{ marginTop: 18 }}>
                        {confirmStop === b.id ? (
                          <>
                            <span className="status">
                              Stop sending the remaining {count}? What has already gone out stays
                              sent — this can be finished later by hand if that turns out to be
                              wrong.
                            </span>
                            <button className="btn" disabled={!!busy} onClick={() => void stop(b)}>
                              {busy === b.id + 'stop' ? 'Stopping…' : 'Yes, stop it'}
                            </button>
                            <button className="btn ghost" onClick={() => setConfirmStop(null)}>
                              Keep going
                            </button>
                          </>
                        ) : (
                          <button
                            className="btn ghost sm"
                            disabled={!!busy}
                            onClick={() => setConfirmStop(b.id)}
                          >
                            Stop this send
                          </button>
                        )}
                      </div>
                    )}

                    {/*
                      Delete is a two-step, sits away from the test button, and
                      is refused outright once a send exists — deleting the
                      campaign cascades to the send log, which is the only
                      record of who received it. A stopped send is still a
                      send that exists, so it stays undeletable too — Hide
                      (above) is the archive path once it's stopped.
                    */}
                    <div className="savebar" style={{ marginTop: occ && !stopped ? 8 : 18 }}>
                      {occ ? (
                        <span className="hint">
                          This cannot be deleted, because a send of it has been started and its
                          record of who received it is kept.
                        </span>
                      ) : confirmDelete === b.id ? (
                        <>
                          <span className="status">Delete "{b.name}" and its saved list of people?</span>
                          <button className="btn" disabled={!!busy} onClick={() => void remove(b)}>
                            {busy === b.id + 'del' ? 'Deleting…' : 'Yes, delete it'}
                          </button>
                          <button className="btn ghost" onClick={() => setConfirmDelete(null)}>
                            Keep it
                          </button>
                        </>
                      ) : (
                        <button
                          className="btn ghost sm"
                          disabled={!!busy}
                          onClick={() => setConfirmDelete(b.id)}
                        >
                          Delete this
                        </button>
                      )}
                    </div>
                  </>
                )}
              </>
            )}
          </section>
        )
      })}
    </div>
  )
}

/**
 * The words next to the Send button while a send is running — "Keep this
 * page open" plus a live estimate, not a fixed "a couple of minutes"
 * assumption. `estimateRemainingMs` needs two pace samples with real
 * elapsed time and real progress between them; until that exists (the very
 * first tick, or straight after a resume with no new samples yet) this
 * says "estimating…" rather than guessing.
 */
function sendingStatus(prog: Progress | undefined, samples: PaceSample[] | undefined): string {
  if (!prog || prog.pending <= 0) return 'Keep this page open — almost done.'
  const ms = estimateRemainingMs(samples ?? [], prog.pending)
  if (ms === null) return 'Keep this page open — estimating…'
  return `Keep this page open — ${formatEta(ms)}.`
}

/**
 * The third of the three bulk-send additions: a desktop notification on
 * finish or failure, for when the tab is open but she has switched away to
 * something else. A courtesy, not a guarantee — no permission, no
 * `Notification` support, or the constructor itself throwing (Safari's
 * quirks here are well known) all fail silently rather than taking the send
 * down with them; the on-screen outcome text is still the record of what
 * happened either way.
 */
function notifySendResult(b: SavedBlast, r: SendOutcome): void {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return
  /**
   * THREE outcomes, not two. A rehearsal (`SEND_DISABLED`) is `ok: true`
   * with `progress.done` still `false` — there is genuinely nothing to mark
   * done — so folding it into the plain `finished` check below used to read
   * a successful, deliberate rehearsal as "needs attention", the same
   * wording a real failure gets. Caught in adversarial review pass 1.
   * Checked first and on its own, with neutral wording that is neither
   * "sent" (it wasn't) nor "needs attention" (nothing is wrong).
   */
  if (r.dryRun) {
    try {
      new Notification('Bulk email — sending is off', { body: `"${b.name}": ${r.error ?? 'nothing was sent.'}` })
    } catch {
      // See the comment below — a notification failing is not a send failing.
    }
    return
  }
  const finished = r.ok && r.progress.done
  const title = finished ? 'Bulk email sent' : 'Bulk email needs attention'
  const body = finished
    ? `"${b.name}" finished — ${r.progress.sent} of ${r.progress.total} sent.`
    : `"${b.name}": ${r.error ?? 'something went wrong.'}`
  try {
    new Notification(title, { body })
  } catch {
    // See the comment above — a notification failing is not a send failing.
  }
}

/**
 * What moved since it was saved, in names where the list is short enough to
 * read. A count alone invites her to accept it; a name invites her to check it.
 */
function DriftNote({ drift }: { drift: Drift }) {
  if (!hasDrift(drift)) return null
  const names = (list: { first_name: string | null; last_name: string | null }[]) =>
    list.length <= 4
      ? ` — ${list.map((a) => `${a.first_name ?? ''} ${a.last_name ?? ''}`.trim()).join(', ')}`
      : ''
  return (
    <ul className="hint" style={{ margin: '6px 0 0 16px' }}>
      {drift.departed.length > 0 && (
        <li>{drift.departed.length} have left the brokerage since you saved this{names(drift.departed)}</li>
      )}
      {drift.suppressed.length > 0 && (
        <li>{drift.suppressed.length} are tagged "no bulk emails"{names(drift.suppressed)}</li>
      )}
      {drift.noEmail.length > 0 && (
        <li>{drift.noEmail.length} have no usable email address{names(drift.noEmail)}</li>
      )}
      {drift.missing.length > 0 && <li>{drift.missing.length} are no longer in the roster at all</li>}
    </ul>
  )
}
